import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Upload, File, X, Eye, Download, Plus, Trash2, Building2 } from 'lucide-react';

const RequestForms = () => {
  const [requestForms, setRequestForms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [viewingFileUrl, setViewingFileUrl] = useState(null);
  const [viewingFileType, setViewingFileType] = useState(null);
  const [filterFacility, setFilterFacility] = useState('');

  // Upload form state
  const [uploadData, setUploadData] = useState({
    title: '',
    file: null,
    facilityId: '',
    description: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchRequestForms();
    fetchFacilities();
  }, [filterFacility]);

  const fetchRequestForms = async () => {
    try {
      setLoading(true);
      const params = filterFacility ? { facilityId: filterFacility } : {};
      const response = await api.get('/request-forms', { params });
      setRequestForms(response.data.data);
    } catch (error) {
      console.error('Error fetching request forms:', error);
      toast.error('Failed to load request forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Allow common document and image types
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Only images, PDF, Word, Excel, and text files are allowed');
        return;
      }
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setUploadData({ ...uploadData, file: selectedFile });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('title', uploadData.title.trim());
      if (uploadData.facilityId) {
        formData.append('facilityId', uploadData.facilityId);
        const selectedFacility = facilities.find(f => f.id.toString() === uploadData.facilityId);
        if (selectedFacility) {
          formData.append('facilityName', selectedFacility.name);
        }
      }
      if (uploadData.description) {
        formData.append('description', uploadData.description);
      }

      await api.post('/request-forms', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Request form uploaded successfully');
      setUploadData({
        title: '',
        file: null,
        facilityId: '',
        description: ''
      });
      setShowUpload(false);
      fetchRequestForms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload request form');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (form) => {
    try {
      // Fetch the file as blob using api instance
      const response = await api.get(`/request-forms/${form.id}/view`, {
        responseType: 'blob',
      });
      
      // Check if response is actually an error
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Failed to load file');
      }
      
      // Create blob URL for viewing
      const blobUrl = URL.createObjectURL(response.data);
      setViewingFileUrl(blobUrl);
      setViewingFileType(form.fileType?.toLowerCase());
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error(error.response?.data?.message || 'Failed to view file');
    }
  };

  const handleDownload = async (form) => {
    try {
      // Fetch the file as blob using api instance
      const response = await api.get(`/request-forms/${form.id}/download`, {
        responseType: 'blob',
      });
      
      // Check if response is actually an error
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Failed to download file');
      }
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = form.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error(error.response?.data?.message || 'Failed to download file');
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Are you sure you want to delete "${form.title}"?`)) {
      return;
    }

    try {
      await api.delete(`/request-forms/${form.id}`);
      toast.success('Request form deleted successfully');
      fetchRequestForms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete request form');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Request Forms</h1>
        <p className="text-gray-600">
          Upload and manage official request forms from facilities (e.g., on letterhead)
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <select
            value={filterFacility}
            onChange={(e) => setFilterFacility(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Facilities</option>
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showUpload ? (
            <>
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span>Upload Request Form</span>
            </>
          )}
        </button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="mb-6 bg-gray-50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload New Request Form</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter request form title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facility (Optional)
              </label>
              <select
                value={uploadData.facilityId}
                onChange={(e) => setUploadData({ ...uploadData, facilityId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a facility</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".jpeg,.jpg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    className="hidden"
                  />
                  <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
                    <Upload className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {uploadData.file ? uploadData.file.name : 'Click to select file'}
                    </span>
                  </div>
                </label>
                {uploadData.file && (
                  <button
                    type="button"
                    onClick={() => setUploadData({ ...uploadData, file: null })}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Maximum file size: 10MB. Allowed types: Images, PDF, Word, Excel, Text
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter description or notes about this request form"
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowUpload(false);
                  setUploadData({
                    title: '',
                    file: null,
                    facilityId: '',
                    description: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || !uploadData.file || !uploadData.title.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                <span>{uploading ? 'Uploading...' : 'Upload'}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Request Forms List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading request forms...</p>
        </div>
      ) : requestForms.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <File className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No request forms uploaded yet</p>
          <p className="text-sm text-gray-400 mt-2">Use the "Upload Request Form" button above to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requestForms.map((form) => (
            <div
              key={form.id}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <File className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium text-gray-900">{form.title}</h4>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {form.fileType}
                    </span>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {formatFileSize(form.fileSize)}
                    </span>
                  </div>
                  {form.facilityName && (
                    <div className="flex items-center space-x-1 mb-2 text-sm text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span>{form.facilityName}</span>
                    </div>
                  )}
                  {form.description && (
                    <p className="text-sm text-gray-600 mb-2">{form.description}</p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Uploaded by: {form.uploadedBy?.firstName} {form.uploadedBy?.lastName}</span>
                    <span>Uploaded: {formatDate(form.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleView(form)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="View"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDownload(form)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(form)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File Viewer Modal */}
      {viewingFileUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Viewing Request Form</h3>
              <button
                onClick={() => {
                  if (viewingFileUrl) {
                    URL.revokeObjectURL(viewingFileUrl);
                  }
                  setViewingFileUrl(null);
                  setViewingFileType(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {viewingFileType && (viewingFileType === 'jpeg' || viewingFileType === 'jpg' || viewingFileType === 'png' || viewingFileType === 'gif' || viewingFileType === 'webp') ? (
                <div className="flex items-center justify-center">
                  <img 
                    src={viewingFileUrl} 
                    alt="Request Form" 
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                </div>
              ) : viewingFileType === 'pdf' ? (
                <div className="w-full h-[70vh]">
                  <iframe 
                    src={viewingFileUrl} 
                    className="w-full h-full border-0"
                    title="PDF Viewer"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[70vh]">
                  <File className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">This file type cannot be previewed.</p>
                  <a
                    href={viewingFileUrl}
                    download
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestForms;
